import { LivestockWeightType } from "constants/livestock";
import functionUtils from "shared/functionUtils";
import type {
  AgeRange,
  EnrichedAnimalCategory,
  EnrichedHerdRecord,
} from "shared/types";
import BaseService from "../BaseService";
import type { DataResult } from "../services.types";
import { composeMixins } from "../services.utils";
import { AgeRangeServiceMixin } from "./ageRangeService";
import { CategoryServiceMixin } from "./categoryService";
import { CauseOfDeathServiceMixin } from "./causeOfDeathService";
import { GoalServiceMixin } from "./goalService";
import { HerdRecordsServiceMixin } from "./herdRecordsService";
import {
  type AccessoryWeightPayload,
  LivestockServiceMixin,
} from "./livestockService";
import { OperationTypeServiceMixin } from "./operationTypeService";
import { ProjectedWeightServiceMixin } from "./projectedWeightService";
import { WeightGainCurveServiceMixin } from "./weightGainCurveService";
import { WeightServiceMixin } from "./weightService";

export class HerdService extends composeMixins(
  AgeRangeServiceMixin,
  CategoryServiceMixin,
  CauseOfDeathServiceMixin,
  GoalServiceMixin,
  HerdRecordsServiceMixin,
  LivestockServiceMixin,
  OperationTypeServiceMixin,
  ProjectedWeightServiceMixin,
  WeightGainCurveServiceMixin,
  WeightServiceMixin
)(BaseService) {
  async fetchEnrichedFarmCategories(
    farmId: number,
    withDeleted = true
  ): Promise<DataResult<EnrichedAnimalCategory>> {
    const [farmCategoriesResult, ageRangesResult, weightGainCurvesResult] =
      await Promise.all([
        this.fetchFarmCategories(farmId, withDeleted),
        this.listAgeRanges(),
        this.listCurvesForFarm(farmId),
      ]);

    const ageRangeMap = new Map(
      ageRangesResult.data.map((item) => [item.id, item])
    );
    const weightGainCurveMap = new Map(
      weightGainCurvesResult.data.map((item) => [item.id, item])
    );

    const enrichedCategories =
      farmCategoriesResult.data?.map<EnrichedAnimalCategory>((item) => ({
        ...item,
        ageRange: ageRangeMap.get(item.ageRangeId) as AgeRange,
        warnings: item.warnings !== "" ? item.warnings : null,
        weightGainCurve:
          weightGainCurveMap.get(item.weightGainCurveId ?? -1) ?? null,
      })) ?? [];

    return { data: enrichedCategories, total: farmCategoriesResult.total };
  }

  async fetchEnrichedFarmMovements(
    farmId: number
  ): Promise<DataResult<EnrichedHerdRecord>> {
    const [herdRecordsResult, farmCategoriesResult, operationTypesResult] =
      await Promise.all([
        this.listFarmRecords(),
        this.fetchFarmCategories(farmId, true),
        this.listOperationTypes(),
      ]);

    const farmCategoryMap = new Map(
      farmCategoriesResult.data.map((item) => [item.id, item])
    );
    const operationTypeMap = new Map(
      operationTypesResult.data.map((item) => [item.id, item])
    );

    const enrichedRecords =
      herdRecordsResult.data?.map<EnrichedHerdRecord>((record) => ({
        ...record,
        animalCategory:
          farmCategoryMap.get(record.animalCategoryId ?? -1) ?? null,
        operationType:
          operationTypeMap.get(record.operationTypeId ?? -1) ?? null,
      })) ?? [];

    return { data: enrichedRecords, total: herdRecordsResult.total };
  }

  async updateLots(
    payload: Array<{
      id: number;
      goalId: number | null;
      goalWeight: { id: number | null; value: number | null };
      measuredWeight: {
        id: number | null;
        value: number | null;
        operationDate: string | null;
      };
    }>
  ): Promise<void> {
    const { deleteWeights, updateAccessories } = payload.reduce(
      (acc, item) => {
        const weights = [] as AccessoryWeightPayload[];

        if (item.goalWeight.id != null && item.goalWeight.value == null) {
          acc.deleteWeights.push(item.goalWeight.id);
        }

        if (item.goalWeight.value != null) {
          weights.push({
            weightType: LivestockWeightType.Goal,
            weight: item.goalWeight.value,
          });
        }

        if (
          item.measuredWeight?.id != null &&
          item.measuredWeight.value == null
        ) {
          acc.deleteWeights.push(item.measuredWeight.id);
        }

        if (item.measuredWeight?.value != null) {
          weights.push({
            weightType: LivestockWeightType.Measured,
            weight: item.measuredWeight.value,
            operationDate: item.measuredWeight.operationDate ?? undefined,
          });
        }

        acc.updateAccessories.push({
          livestockId: item.id,
          accessories: { goalId: item.goalId, weights },
        });

        return acc;
      },
      {
        deleteWeights: [] as number[],
        updateAccessories: [] as Parameters<typeof this.updateAccessories>[0],
      }
    );

    await Promise.all([
      functionUtils.executeAsyncWhen(
        async () => {
          await this.finishWeights(deleteWeights);
        },
        { when: deleteWeights.length > 0 }
      ),
      functionUtils.executeAsyncWhen(
        async () => {
          await this.updateAccessories(updateAccessories);
        },
        { when: updateAccessories.length > 0 }
      ),
    ]);
  }
}
